import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable, LOCALE_ID } from "@angular/core";
import { STORE_NAME_PRODUCTS } from "@app/indexed-db-config";
import { ReadOnlyTokenStorage } from "@app/read-only-token.storage";
import { LastProductUpdateService } from "@domain/product/last-product-update.service";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductCountryService } from "@domain/product/product-country.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductLanguageService } from "@domain/product/product-language.service";
import { ProductMapperService } from "@domain/product/product-mapper.service";
import { environment } from "@environments/environment";
import { ObservableInstanceMapper } from "@utils/observable-instance-mapper";
import { instanceToPlain } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { first, forkJoin, map, Observable, of, Subject, switchMap, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ProductService {
  private http = inject(HttpClient);
  private dbService = inject(NgxIndexedDBService);
  private productMapperService = inject(ProductMapperService);
  private productCountryService = inject(ProductCountryService);
  private productLanguageService = inject(ProductLanguageService);
  private productUpdateService = inject(LastProductUpdateService);
  private readOnlyTokenStorage = inject(ReadOnlyTokenStorage);
  private localeId = inject(LOCALE_ID);
  private productImageUrlCache = new Map<string, string | null>();
  private refreshCompleted$ = new Subject<string[]>();

  public static readonly PRODUCTS_URL = environment.apiUrl + "/product/products";
  public static readonly PRODUCT_PROPERTIES_URL = environment.apiUrl + "/product-property/product-properties";
  private _filterCountry?: string;

  init(): Observable<Product[]> {
    // Country for read only access needs to be loaded separately since it is in the URL
    if (this.readOnlyTokenStorage.get()) {
      const urlParams = new URLSearchParams(window.location.hash.split("?")[1]);
      const country = urlParams.get("country");
      if (country) {
        this.productCountryService.selectCountry(country);
      }
    }

    this.productLanguageService.selectLanguage(this.localeId);
    this.productCountryService.newCountry$.subscribe(() => {
      this.clearProductImageCache();
      this.refreshAll().pipe(first()).subscribe();
    });

    return this.loadAll().pipe(
      switchMap((products) => {
        if (
          !products.length ||
          !this.areProductNamesAvailableForLocale(products, this.localeId) ||
          !this.areProjectCountriesAvailable(products) ||
          this.hasOutdatedPriceDataFormat(products)
        ) {
          return this.refreshAll();
        }
        this.populateProductImageCache(products);
        return of(products);
      }),
    );
  }

  getAvailableProducts(...productTypes: FgdsProductType[]): Observable<Product[]> {
    return ObservableInstanceMapper.valuesToInstance(this.dbService.getAll<Product>(STORE_NAME_PRODUCTS), Product).pipe(
      map((products: Product[]) =>
        products.filter(
          (product) =>
            (productTypes.length === 0 || productTypes.includes(product.type)) &&
            product.isAvailable &&
            this.isAvailableInCountry(product),
        ),
      ),
    );
  }

  getProductById(id: string): Observable<Product | undefined> {
    return ObservableInstanceMapper.valueToInstance(this.dbService.getByKey<Product>(STORE_NAME_PRODUCTS, id), Product).pipe(
      map((product) => {
        if (!product) {
          console.error(`No product found for id ${id}`);
          return undefined;
        }

        if (!this.isAvailableInCountry(product)) {
          console.error(`Product with id  ${id} is not available for country ${this._filterCountry}`);
          return undefined;
        }
        return product;
      }),
    );
  }

  getProductNameById(id: string): Observable<string | undefined> {
    return this.getProductById(id).pipe(map((product) => product?.getLocalizedName(this.localeId)));
  }

  getCantoImageUrl(productId: string): Observable<string | undefined> {
    if (this.productImageUrlCache.has(productId)) {
      const cachedUrl = this.productImageUrlCache.get(productId) ?? undefined;
      return of(cachedUrl);
    }

    return this.getProductById(productId).pipe(
      map((product) => {
        const url = product?.cantoImageId ? ProductImage.getCantoSourceUrl(product.cantoImageId) : undefined;
        this.productImageUrlCache.set(productId, url ?? null);
        return url;
      }),
    );
  }

  getCantoImageUrlFromCache(productId: string): string | undefined {
    if (!this.productImageUrlCache.has(productId)) {
      return undefined;
    }
    return this.productImageUrlCache.get(productId) ?? undefined;
  }

  getProductsByIds(ids: string[], productType?: FgdsProductType): Observable<Product[]> {
    return ObservableInstanceMapper.valuesToInstance(this.dbService.bulkGet<Product>(STORE_NAME_PRODUCTS, ids), Product).pipe(
      map((products: Product[]) =>
        products.filter((product) => (!productType || product?.type === productType) && this.isAvailableInCountry(product)),
      ),
    );
  }

  getProductNamesByIds(ids: string[], productType?: FgdsProductType): Observable<string[]> {
    return this.getProductsByIds(ids, productType).pipe(
      map((products) => products.map((product) => product.getLocalizedName(this.localeId))),
    );
  }

  getDiscontinuedProductIds(): Observable<string[]> {
    return ObservableInstanceMapper.valuesToInstance(this.dbService.getAll<Product>(STORE_NAME_PRODUCTS), Product).pipe(
      map((products: Product[]) =>
        products.filter((product) => !product.isAvailable && this.isAvailableInCountry(product)).map((product) => product.id),
      ),
    );
  }

  getAllProductIds(): Observable<string[]> {
    return ObservableInstanceMapper.valuesToInstance(this.dbService.getAll<Product>(STORE_NAME_PRODUCTS), Product).pipe(
      map((products) => products.filter((product) => this.isAvailableInCountry(product))),
      map((products: Product[]) => products.map((product) => product.id)),
    );
  }

  refreshAll(): Observable<Product[]> {
    const refreshedCountries = [...new Set(this.productCountryService.countries)];
    const productRequests$ = new Array<Observable<{ language: string; country: string; products: Product[] }>>();
    refreshedCountries.forEach((country) =>
      new Set<string>(this.productLanguageService.languages).forEach((language) => {
        productRequests$.push(
          this.loadProductsFromApi(language, country).pipe(
            map((products) => {
              return { products, language, country };
            }),
          ),
        );
      }),
    );
    return forkJoin(productRequests$).pipe(
      map((products) => this.mergeProductLists(products)),
      switchMap((products) => this.clearAll().pipe(switchMap(() => this.storeProductsInDB(products)))),
      tap((products) => {
        this.populateProductImageCache(products);
        this.refreshCompleted$.next(refreshedCountries);
      }),
    );
  }

  getProductCountries(): Observable<string[]> {
    return this.loadProductsFromDB().pipe(
      map((products) => [...new Set<string>(products.flatMap((product) => product.countryData.map((cd) => cd.countryCode)))]),
    );
  }

  clearAll(): Observable<void> {
    this.clearProductImageCache();
    return this.dbService.clear(STORE_NAME_PRODUCTS);
  }

  filter(products$: Observable<Product[]>, query: string): Observable<any> {
    return products$.pipe(map((products) => products.filter((product) => this.searchFilter(product, query))));
  }

  searchFilter(product: Product, query: string): boolean {
    return (
      product.getLocalizedName(this.localeId).toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) >= 0 ||
      (product.id.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) >= 0 && this.isAvailableInCountry(product))
    );
  }

  updateFilterCountry(country: string) {
    this._filterCountry = this.normalizeCountry(country);
    this.clearProductImageCache();
  }

  getFilterCountry(): string {
    return this._filterCountry ?? "";
  }

  getPriceData(product: Product) {
    return product.getPriceDataByCountry(this._filterCountry!);
  }

  getZrefPriceData(product: Product) {
    return product.getZrefPriceDataByCountry(this._filterCountry!);
  }

  get refreshedData$() {
    return this.refreshCompleted$.asObservable();
  }

  private loadAll(): Observable<Product[]> {
    return this.dbService
      .count(STORE_NAME_PRODUCTS)
      .pipe(switchMap((count: number) => (count > 0 ? this.loadProductsFromDB() : of([]))));
  }

  private loadProductsFromApi(languageString: string, country: string): Observable<Product[]> {
    const productParams = new HttpParams().set("lang", languageString).set("country", country);
    const productPropertiesParams = new HttpParams().set("lang", languageString);

    return forkJoin([
      this.http.get(ProductService.PRODUCTS_URL, { params: productParams }),
      this.http.get(ProductService.PRODUCT_PROPERTIES_URL, { params: productPropertiesParams }),
    ]).pipe(
      map((results: any[]) => {
        const productsResponse = results[0];
        const productPropsResponse = results[1];
        return this.productMapperService.mapProductAndProperties(productsResponse, productPropsResponse);
      }),
    );
  }

  private loadProductsFromDB(): Observable<Product[]> {
    return ObservableInstanceMapper.valuesToInstance(this.dbService.getAll<Product>(STORE_NAME_PRODUCTS), Product);
  }

  private mergeProductLists(
    productsByLanguageAndCountry: {
      language: string;
      country: string;
      products: Product[];
    }[],
  ): Product[] {
    const resultingProducts = new Map<string, Product>();
    productsByLanguageAndCountry.forEach((productByLanguageAndCountry) => {
      if (productByLanguageAndCountry.products.length !== 0) {
        productByLanguageAndCountry.products.forEach((product) => {
          let resultingProduct = resultingProducts.get(product.id);
          if (!resultingProduct) {
            resultingProduct = product;
            resultingProducts.set(product.id, resultingProduct);
          }
          resultingProduct.addLocalizedName(productByLanguageAndCountry.language, product.name);
          resultingProduct.addCountryData(productByLanguageAndCountry.country, product.countryData[0]);
        });
      }
    });
    const products = Array.from(resultingProducts.values());
    products.forEach((product) => product.distributeZrefPrices());
    return products;
  }

  private storeProductsInDB(data: Product[]): Observable<Product[]> {
    if (data.length > 0) {
      return this.dbService
        .bulkPut(
          STORE_NAME_PRODUCTS,
          data.map((product) => instanceToPlain(product)),
        )
        .pipe(
          switchMap(() => this.productUpdateService.saveLastUpdate()),
          map(() => data),
        );
    }
    return of([]);
  }

  private hasOutdatedPriceDataFormat(products: Product[]): boolean {
    return products.some((product) => product.countryData.some((cd) => !Array.isArray((cd as any).prices)));
  }

  private isAvailableInCountry(product: Product) {
    if (!this._filterCountry) {
      return true;
    }
    return product.isAvailableInCountry(this._filterCountry);
  }

  private areProductNamesAvailableForLocale(products: Product[], locale: string) {
    return products.every((product) => !!product.getLocalizedName(locale, undefined));
  }

  private areProjectCountriesAvailable(products: Product[]) {
    const availableCountries = new Set(
      products.flatMap((product) => product.countryData.map((countryData) => this.normalizeCountry(countryData.countryCode))),
    );

    return this.productCountryService.countries
      .map((country) => this.normalizeCountry(country))
      .filter(Boolean)
      .every((country) => availableCountries.has(country));
  }

  private populateProductImageCache(products: Product[]) {
    this.productImageUrlCache.clear();
    products.forEach((product) => {
      if (!this.isAvailableInCountry(product)) {
        this.productImageUrlCache.set(product.id, null);
        return;
      }
      const url = product.cantoImageId ? ProductImage.getCantoSourceUrl(product.cantoImageId) : null;
      this.productImageUrlCache.set(product.id, url);
    });
  }

  private clearProductImageCache() {
    this.productImageUrlCache.clear();
  }

  private normalizeCountry(country: string) {
    return country?.trim().toUpperCase() ?? "";
  }
}
